import React, { useState, useEffect } from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

import imgMaria from "../static/media/Maria.webp";
import imgDaniel from "../static/media/Daniel.webp";
import imgFernanda from "../static/media/Fernanda.webp";

const MotionBox = motion(Box);

// ── Datos de testimonios ──
const testimonials = [
  {
    id: 1,
    image: imgMaria,
    quote:
      "Lleva el control del inventario y la facturación de manera sencilla. Antes tardaba 2 días en hacer el inventario, ahora lo hago en 2 horas.",
    author: "María G.",
    role: "Dueña de Ferretería",
    business: "Ferretería",
    tag: "Inventario + Facturación",
    metric: "12h/sem",
    metricLabel: "ahorradas",
    accent: "#2C5FBE",
    bgGradient: "linear-gradient(135deg, #2C5FBE 0%, #1A3D8F 100%)",
  },
  {
    id: 2,
    image: imgDaniel,
    quote:
      "El sistema te permite generar las órdenes de pedido, facturas y proformas de manera rápida y sencilla. Antes tardaba 3 días en hacer las proformas, ahora lo hago en horas.",
    author: "Daniel R.",
    role: "Administrador de Restaurantes",
    business: "Restaurantes",
    tag: "Proformas",
    metric: "−85%",
    metricLabel: "tiempo de facturación",
    accent: "#0D5DB5",
    bgGradient: "linear-gradient(135deg, #0D5DB5 0%, #0B3E7A 100%)",
  },
  {
    id: 3,
    image: imgFernanda,
    quote:
      "El equipo de soporte nos configuró todo en menos de un día. Al siguiente ya estábamos emitiendo facturas electrónicas sin ningún problema. Vale cada centavo.",
    author: "Fernanda L.",
    role: "Gerente de Empresa",
    business: "Empresa",
    tag: "Implementación rápida",
    metric: "<24h",
    metricLabel: "para estar operativos",
    accent: "#4F8CFF",
    bgGradient: "linear-gradient(135deg, #4F8CFF 0%, #2C5FBE 100%)",
  },
];

// ── Estrellas ──
function StarRating() {
  return (
    <Box sx={{ display: "flex", gap: 0.35 }}>
      {[...Array(5)].map((_, i) => (
        <svg key={i} width="15" height="15" viewBox="0 0 18 18" fill="none">
          <path
            d="M9 2l1.8 4.1L15 6.6l-3 3 .7 4.4L9 12l-3.7 2 .7-4.4-3-3 4.2-.5L9 2z"
            fill="#F59E0B"
          />
        </svg>
      ))}
    </Box>
  );
}

// ── Tarjeta lateral compacta ──
function SideCard({ item, isActive, index, onClick }) {
  return (
    <MotionBox
      onClick={onClick}
      initial={{ opacity: 0, x: 30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12, duration: 0.45, ease: "easeOut" }}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      sx={{
        position: "relative",
        p: 2.5,
        borderRadius: "18px",
        cursor: "pointer",
        overflow: "hidden",
        bgcolor: isActive ? "#fff" : "rgba(255,255,255,0.55)",
        border: "1px solid",
        borderColor: isActive ? "rgba(44,95,190,0.25)" : "rgba(44,95,190,0.08)",
        boxShadow: isActive
          ? "0 12px 32px rgba(44,95,190,0.1)"
          : "0 2px 8px rgba(15,23,42,0.03)",
        backdropFilter: isActive ? "none" : "blur(4px)",
        transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Barra lateral activa */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 12,
          bottom: 12,
          width: isActive ? 3 : 0,
          borderRadius: "0 3px 3px 0",
          bgcolor: item.accent,
          transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />

      {/* Header: avatar + info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
        {/* Avatar circular */}
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            flexShrink: 0,
            overflow: "hidden",
            transition: "all 0.35s ease",
            boxShadow: isActive
              ? `0 6px 16px ${item.accent}30`
              : "0 2px 6px rgba(44,95,190,0.06)",
            border: isActive
              ? `2px solid ${item.accent}`
              : "2px solid transparent",
          }}
        >
          <Box
            component="img"
            src={item.image}
            alt={item.author}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
            }}
          />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.88rem",
              color: isActive ? "#1A2B4A" : "#475569",
              lineHeight: 1.2,
              mb: 0.2,
              transition: "color 0.3s ease",
            }}
          >
            {item.author}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: isActive ? "#64748B" : "#94A3B8",
              lineHeight: 1.2,
              transition: "color 0.3s ease",
            }}
          >
            {item.role}
          </Typography>
        </Box>

        {/* Estrellas pequeñas */}
        <Box sx={{ ml: "auto", flexShrink: 0 }}>
          <StarRating />
        </Box>
      </Box>

      {/* Cita resumida */}
      <Typography
        sx={{
          fontSize: "0.82rem",
          color: isActive ? "#475569" : "#64748B",
          lineHeight: 1.55,
          pl: 0.5,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          fontStyle: "italic",
          transition: "color 0.3s ease",
          "&::before": {
            content: '"\\201C"',
            color: item.accent,
            fontWeight: 800,
            fontSize: "1.1rem",
            opacity: isActive ? 1 : 0.4,
            transition: "opacity 0.3s ease",
          },
        }}
      >
        {item.quote}
      </Typography>

      {/* Barra de progreso cuando está activo */}
      {isActive && (
        <Box
          sx={{
            mt: 2,
            height: 3,
            bgcolor: "rgba(44,95,190,0.06)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <MotionBox
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 6, ease: "linear" }}
            sx={{
              height: "100%",
              background: `linear-gradient(90deg, ${item.accent} 0%, ${item.accent}88 100%)`,
              borderRadius: 2,
            }}
          />
        </Box>
      )}
    </MotionBox>
  );
}

// ── COMPONENTE PRINCIPAL ──
export default function TestimonialsSection() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12, lg: 14 },
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #F4F7FF 40%, #EDF3FE 100%)",
      }}
    >
      {/* ── Decoraciones geométricas de fondo ── */}
      {/* Círculo grande difuminado */}
      <Box
        sx={{
          position: "absolute",
          top: -120,
          right: -80,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(44,95,190,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Rombo decorativo */}
      <Box
        sx={{
          position: "absolute",
          left: { xs: -150, md: -60 },
          bottom: -100,
          width: 400,
          height: 400,
          pointerEvents: "none",
          opacity: 0.04,
        }}
      >
        <svg viewBox="0 0 400 400" fill="none">
          <polygon points="200,10 390,200 200,390 10,200" fill="#2C5FBE" />
        </svg>
      </Box>
      {/* Círculo punteado */}
      <Box
        sx={{
          position: "absolute",
          right: { xs: -60, md: "5%" },
          top: "40%",
          width: 180,
          height: 180,
          borderRadius: "50%",
          border: "1.5px dashed rgba(44,95,190,0.07)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* ── Header ── */}
        <MotionBox
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            mb: { xs: 6, md: 8 },
            maxWidth: 650,
            textAlign: { xs: "left", md: "center" },
            mx: { md: "auto" },
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2rem", sm: "2.4rem", md: "2.8rem" },
              color: "#1A2B4A",
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            Negocios que son{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #2C5FBE 0%, #4F8CFF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              compatibles con FactuSysDR
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "#475569",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.65,
              maxWidth: 500,
              mx: { md: "auto" },
            }}
          >
            Descubre cómo FactuSysDR está transformando la operación diaria de
            nuestros clientes.
          </Typography>
        </MotionBox>

        {/* ── Grid: Principal + Sidebar ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" },
            gap: { xs: 4, lg: 4 },
            alignItems: "start",
          }}
        >
          {/* ── Tarjeta principal destacada ── */}
          <AnimatePresence mode="wait">
            <MotionBox
              key={testimonials[active].id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              sx={{
                borderRadius: "28px",
                p: { xs: 4, md: 5.5 },
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                minHeight: { xs: "auto", md: 440 },
                background: testimonials[active].bgGradient,
                boxShadow: `0 24px 48px ${testimonials[active].accent}20`,
              }}
            >
              {/* Decoraciones internas */}
              <Box
                sx={{
                  position: "absolute",
                  top: -60,
                  right: -60,
                  width: 260,
                  height: 260,
                  opacity: 0.06,
                  pointerEvents: "none",
                }}
              >
                <svg viewBox="0 0 260 260" fill="none">
                  <polygon
                    points="130,8 248,76 248,184 130,252 12,184 12,76"
                    fill="white"
                  />
                </svg>
              </Box>

              {/* Contenido */}
              <Box sx={{ position: "relative", zIndex: 1, flexGrow: 1 }}>
                {/* Tag + Stars */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3.5,
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.6,
                      bgcolor: "rgba(255,255,255,0.15)",
                      borderRadius: "20px",
                      backdropFilter: "blur(4px)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.95)",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      {testimonials[active].tag}
                    </Typography>
                  </Box>
                  <StarRating />
                </Box>

                {/* Comilla gigante */}
                <Typography
                  sx={{
                    fontSize: "5rem",
                    lineHeight: 0.6,
                    color: "rgba(255,255,255,0.15)",
                    fontWeight: 900,
                    fontFamily: "Georgia, serif",
                    mb: -1,
                    ml: -0.5,
                  }}
                >
                  "
                </Typography>

                {/* Cita */}
                <Typography
                  sx={{
                    color: "white",
                    fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.35rem" },
                    fontWeight: 400,
                    lineHeight: 1.7,
                    mb: 5,
                  }}
                >
                  {testimonials[active].quote}
                </Typography>
              </Box>

              {/* Autor + Métrica */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  position: "relative",
                  zIndex: 1,
                  pt: 3,
                  borderTop: "1px solid rgba(255,255,255,0.1)",
                  flexWrap: "wrap",
                  gap: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "16px",
                      overflow: "hidden",
                      flexShrink: 0,
                      border: "2px solid rgba(255,255,255,0.25)",
                    }}
                  >
                    <Box
                      component="img"
                      src={testimonials[active].image}
                      alt={testimonials[active].author}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center top",
                      }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "white",
                        mb: 0.2,
                      }}
                    >
                      {testimonials[active].author}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        color: "rgba(255,255,255,0.65)",
                      }}
                    >
                      {testimonials[active].role}
                    </Typography>
                  </Box>
                </Box>

                {/* Métrica */}
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "1.7rem", md: "2rem" },
                      color: "white",
                      lineHeight: 1,
                      mb: 0.4,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {testimonials[active].metric}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "rgba(255,255,255,0.6)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      fontWeight: 600,
                    }}
                  >
                    {testimonials[active].metricLabel}
                  </Typography>
                </Box>
              </Box>
            </MotionBox>
          </AnimatePresence>

          {/* ── Sidebar: tarjetas compactas ── */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
            {testimonials.map((t, i) => (
              <SideCard
                key={t.id}
                item={t}
                isActive={i === active}
                index={i}
                onClick={() => setActive(i)}
              />
            ))}

            {/* Mini card: indicador de navegación */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
                mt: 1.5,
              }}
            >
              {testimonials.map((t, i) => (
                <Box
                  key={t.id}
                  onClick={() => setActive(i)}
                  sx={{
                    width: i === active ? 28 : 8,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: i === active ? t.accent : "rgba(44,95,190,0.15)",
                    cursor: "pointer",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor: i === active ? t.accent : "rgba(44,95,190,0.3)",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
